'use client';

import { useEffect, useLayoutEffect, useRef } from 'react';
import type { RefObject } from 'react';
import { useScrollManager } from '../contexts/ScrollManagerContext';
import { useScreenLayout } from '../screen/useScreenLayout';

interface UseScrollRestorationOptions {
  scrollKey: string;
  scrollRef: RefObject<HTMLDivElement | null>;
  enabled?: boolean;
}

export const useScrollRestoration = ({ scrollKey, scrollRef, enabled = true }: UseScrollRestorationOptions) => {
  const { saveScroll, getScroll } = useScrollManager();
  const { layoutReady, isPreview } = useScreenLayout();
  const scrollRestoredRef = useRef(false);

  useLayoutEffect(() => {
    if (!enabled || !scrollRef.current || !layoutReady) return;
    if (scrollRestoredRef.current) return;

    scrollRestoredRef.current = true;
    queueMicrotask(() => {
      if (!scrollRef.current) return;
      const savedPosition = getScroll(scrollKey);
      if (savedPosition > 0) {
        scrollRef.current.scrollTop = savedPosition;
      }
    });
  }, [enabled, scrollKey, scrollRef, getScroll, layoutReady]);

  useEffect(() => {
    if (!enabled || !scrollRef.current || !scrollRestoredRef.current || isPreview) return;

    const element = scrollRef.current;

    const handleScroll = () => {
      saveScroll(scrollKey, element.scrollTop);
    };

    element.addEventListener('scroll', handleScroll, { passive: true });

    return () => {
      element.removeEventListener('scroll', handleScroll);
    };
  }, [enabled, scrollKey, scrollRef, saveScroll, layoutReady, isPreview]);

  useEffect(() => {
    return () => {
      scrollRestoredRef.current = false;
    };
  }, []);
};
